// src/hooks/useAuth.js
// Centralized authentication hook using Redux and storage utilities

import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginSuccess, logout as logoutAction } from "../store/authSlice";
import { authStorage } from "../utils/authStorage";
import axiosInstance from "../services/axiosInstance";
import { toast } from "react-toastify";
import { rejoinCustomerRoom } from "../services/socket"; // ✅ Import socket function
import { ROLES } from "../constants";

export const useAuth = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const authState = useSelector(state => state.auth);

    // Login function
    const login = useCallback(async (credentials, expectedRole = null) => {
        try {
            const response = await axiosInstance.post("/auth/login", credentials);
            const { token, user } = response.data;

            // Role validation if specified
            if (expectedRole !== null) {
                const roleMap = { superadmin: 0, admin: 1, pos: 2, user: 2 };
                const allowedRoles = Array.isArray(expectedRole) ? expectedRole : [roleMap[expectedRole]];

                const isManagerAdmin = expectedRole === "admin"
                    && user.user_type === 2
                    && String(user.role_name || "").toLowerCase() === ROLES.MANAGER.toLowerCase();

                if (!allowedRoles.includes(user.user_type) && !isManagerAdmin) {
                    toast.error("You do not have access to this page", { toastId: "login-error" });
                    return false;
                }
            }

            // Dispatch to Redux store
            dispatch(loginSuccess({ token, user }));

            // Store in appropriate storage
            authStorage.setToken(token);
            authStorage.setUser(user);
            authStorage.setLoggedIn(true);
            if (expectedRole) {
                const context = Array.isArray(expectedRole)
                    ? (expectedRole.includes(2) ? "pos" : "admin")
                    : expectedRole;
                authStorage.setLoginContext(context);
            }

            // ✅ Rejoin socket.io customer room after login
            rejoinCustomerRoom();

            // Ensure customer_id is set in sessionStorage for dropdown compatibility
            if (user.customer_id) {
                sessionStorage.setItem("customer_id", String(user.customer_id));
            }

            // Handle theme if user has one
            if (user.theme) {
                authStorage.setTheme(user.theme);
                // Dispatch theme to Redux for real-time update
                dispatch(require("../store/themeSlice").setTheme(user.theme));
            }

            return true;
        } catch (error) {
            console.error("Login error:", error);
            toast.error(error.response?.data?.message || "Login failed", { toastId: "login-error" });
            return false;
        }
    }, [dispatch]);

    // Logout function
    const logout = useCallback((redirectTo = "/login") => {
        // Get current user for role-based redirect
        const user = authState.user || authStorage.getUser();

        // Dispatch logout action
        dispatch(logoutAction());

        // Clear storage
        authStorage.clear();

        // Role-based redirect if not specified
        if (redirectTo === "/login" && user?.user_type !== undefined) {
            if (user.user_type === 0) redirectTo = "/superadmin/login";
            else if (user.user_type === 1) redirectTo = "/admin/login";
        }

        // Navigate to login
        navigate(redirectTo, { replace: true });
    }, [dispatch, navigate, authState.user]);

    // Check if user has specific role
    const hasRole = useCallback((allowedRoles) => {
        const user = authState.user;
        const userRole = user?.user_type;
        if (userRole !== undefined && allowedRoles.includes(userRole)) return true;
        if (allowedRoles.includes(1)
            && user?.user_type === 2
            && String(user.role_name || "").toLowerCase() === ROLES.MANAGER.toLowerCase()) {
            return true;
        }
        return false;
    }, [authState.user]);

    // Get current user with fallback to storage
    const getCurrentUser = useCallback(() => {
        return authState.user || authStorage.getUser();
    }, [authState.user]);

    // Check if authenticated
    const isAuthenticated = useCallback(() => {
        return authState.isLoggedIn && authState.token && authState.user;
    }, [authState.isLoggedIn, authState.token, authState.user]);

    return {
        // State
        user: authState.user,
        token: authState.token,
        isLoggedIn: authState.isLoggedIn,
        role: authState.role,
        customerId: authState.customer_id,
        isRehydrated: authState.isRehydrated,

        // Actions
        login,
        logout,

        // Utilities
        hasRole,
        getCurrentUser,
        isAuthenticated
    };
};

// Hook for getting user role with localStorage fallback
export const useUserRole = () => {
    const user = useSelector(state => state.auth.user);
    const storedUser = authStorage.getUser();
    const userType = user?.user_type ?? storedUser?.user_type ?? authStorage.getRole();
    const roleName = String(user?.role_name || storedUser?.role_name || "").toLowerCase();
    const loginContext = authStorage.getLoginContext();

    if (loginContext === "pos") {
        return 2;
    }

    if (userType === 2 && roleName === "manager") {
        return 1; // treat manager as admin for UI/menu
    }

    return userType;
};

// Hook for getting customer data (for admins)
export const useCustomerData = () => {
    const customerId = useSelector(state => state.auth.customer_id) || authStorage.getCustomerId();

    return useCallback(() => {
        if (!customerId) return null;
        return authStorage.getCustomerData(customerId);
    }, [customerId]);
};